'use client';

import type {
  Benutzer,
  Rolle,
} from '@/lib/api/auth-types';
import seitenRegistryRoh from '../../../shared/page-registry.json';

type SeitenRegistryEintrag = {
  page_key: string;
  label: string;
  beschreibung: string;
  primary_path: string;
  path_aliases: string[];
  default_roles: Rolle[];
  customizable: boolean;
  startseite_prioritaet: number;
};

const SEITEN_REGISTRY = seitenRegistryRoh as SeitenRegistryEintrag[];

const STANDARD_STARTSEITEN: Record<Rolle, string> = {
  admin: '/steuerung',
  einkauf: '/auftragsbestaetigung',
  qualitaet: '/messberichte',
  logistik: '/lieferungen',
};

const PFAD_ZU_PAGE_KEY = new Map<string, string>();
for (const seite of SEITEN_REGISTRY) {
  PFAD_ZU_PAGE_KEY.set(seite.primary_path, seite.page_key);
  for (const alias of seite.path_aliases) {
    PFAD_ZU_PAGE_KEY.set(alias, seite.page_key);
  }
}

function basisPfadAusPfad(pfad: string): string {
  const segmente = pfad.split('/').filter(Boolean);
  return segmente.length > 0 ? `/${segmente[0]}` : '/';
}

export function pageKeyFuerPfad(pfad: string): string | null {
  return PFAD_ZU_PAGE_KEY.get(basisPfadAusPfad(pfad)) ?? null;
}

export function effektiveSeitenrechte(benutzer: Benutzer | null | undefined): string[] {
  if (!benutzer) return [];
  if (Array.isArray(benutzer.seitenrechte) && benutzer.seitenrechte.length > 0) {
    return benutzer.seitenrechte;
  }
  return SEITEN_REGISTRY
    .filter((seite) => benutzer.rolle === 'admin' || seite.default_roles.includes(benutzer.rolle))
    .map((seite) => seite.page_key);
}

export function hatZugriffAufSeite(
  benutzer: Benutzer | null | undefined,
  pfad: string,
): boolean {
  if (!benutzer) return false;
  const pageKey = pageKeyFuerPfad(pfad);
  if (!pageKey) return false;
  return effektiveSeitenrechte(benutzer).includes(pageKey);
}

export function startseiteFuerBenutzer(
  benutzer: Benutzer | null | undefined,
): string {
  if (!benutzer) return '/login';

  const standardStartseite = STANDARD_STARTSEITEN[benutzer.rolle];
  if (standardStartseite && hatZugriffAufSeite(benutzer, standardStartseite)) {
    return standardStartseite;
  }

  const rechte = new Set(effektiveSeitenrechte(benutzer));
  for (const seite of SEITEN_REGISTRY) {
    if (rechte.has(seite.page_key)) {
      return seite.primary_path;
    }
  }

  return '/login';
}